import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/feedback/toast";
import { saveAuthReturnTo } from "@/lib/authRedirect";
import { clearStaleAuthState } from "@/lib/authState";
import {
  getProviderAuthAdapter,
  type ProviderId,
  type ProviderSignInResult,
} from "@/lib/providerRegistry";

type ProviderSignInTelemetry = {
  started?(provider: ProviderId): void;
  failed?(provider: ProviderId): void;
};

/**
 * Own the provider sign-in lifecycle shared by the login page and direct,
 * provider-labelled entry points.
 *
 * Account linking and reconnecting intentionally do not use this hook: they
 * are different backend intents and must not silently fall back to sign-in.
 */
export function useProviderSignIn(telemetry: ProviderSignInTelemetry = {}) {
  const [loadingProvider, setLoadingProvider] = useState<ProviderId | null>(null);
  const { started, failed } = telemetry;

  // A successful sign-in leaves the loading latch set while the browser
  // navigates away. Clear it if Back/Forward Cache later restores this page.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setLoadingProvider(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const startProviderSignIn = useCallback(
    async (provider: ProviderId, returnTo = "/app"): Promise<ProviderSignInResult | undefined> => {
      const adapter = getProviderAuthAdapter(provider);
      if (!adapter) {
        toast.error({
          title: "Sign-in unavailable",
          description: `DeployCompare cannot sign in with ${provider}.`,
          dedupeKey: `login-${provider}-unavailable`,
        });
        return undefined;
      }

      setLoadingProvider(provider);
      started?.(provider);
      try {
        saveAuthReturnTo(returnTo);
        await clearStaleAuthState({ cancelPendingLink: true, expireServerSession: true });
        const result = await adapter.startSignIn();
        if (result.kind === "popup_blocked") {
          setLoadingProvider(null);
          toast.error({
            title: `${adapter.displayName} sign-in blocked`,
            description: "Allow pop-ups for DeployCompare, then try signing in again.",
            dedupeKey: `login-${provider}-popup-blocked`,
          });
        }
        return result;
      } catch (error) {
        setLoadingProvider(null);
        failed?.(provider);
        toast.error({
          title: `${adapter.displayName} sign-in failed`,
          description:
            error instanceof Error
              ? error.message
              : `Could not start ${adapter.displayName} sign-in.`,
          dedupeKey: `login-${provider}-failed`,
        });
        return undefined;
      }
    },
    [failed, started]
  );

  return { loadingProvider, startProviderSignIn };
}
